<script setup lang="ts">
import { computed, ref, watch } from "vue";
import VChips from "@magnit/core/src/components/VChips/VChips.vue";
import VChipsGroup from "@magnit/core/src/components/VChips/VChipsGroup.vue";
import VCheckbox from "@magnit/core/src/components/VCheckbox/VCheckbox.vue";
import VListItem from "@magnit/core/src/components/VListItem/VListItem.vue";
import VSearch from "@magnit/core/src/containers/VSearch/VSearch.vue";
import VText from "@magnit/core/src/components/VText/VText.vue";
import ChevronRightIcon from "@magnit/icons/src/assets/icons/arrow-right-corner-24.svg";
import ChevronBottomIcon from "@magnit/icons/src/assets/icons/arrow-down-corner-24.svg";
import { isEqual, sortBy } from "lodash-es";
import type {
  IFilterValuesList,
  IGoodsFilterElement,
  IGoodsGridFilter,
} from "../types";

const DEFAULT_ITEMS_DISPLAYED_COUNT = 5;

const props = defineProps<{
  filter: IGoodsGridFilter;
  modal?: boolean;
}>();
const emit = defineEmits<{
  showAll: [];
}>();

const modelValue = defineModel<IFilterValuesList>({ default: [] });

const selected = ref<IFilterValuesList>([...modelValue.value]);
const expanded = ref(false);
const searchValue = ref("");

const onShowAll = () => {
  emit("showAll");
};

const displayed = computed<IGoodsFilterElement[]>(() => {
  return expanded.value
    ? sortBy(
        [...props.filter.elements].filter((item) =>
          item.name
            .toLowerCase()
            .trim()
            .includes(searchValue.value.toLowerCase().trim()),
        ),
        "name",
      )
    : sortBy(
        [...props.filter.elements],
        (i) => !modelValue.value.includes(i.name),
      ).slice(
        0,
        Math.max(modelValue.value.length, DEFAULT_ITEMS_DISPLAYED_COUNT),
      );
});

const onClear = () => {
  modelValue.value = [];
};

const onToggleExpanded = () => {
  expanded.value = !expanded.value;
  if (!expanded.value) {
    searchValue.value = "";
  }
};

watch(selected, (next) => {
  modelValue.value = next;
});

watch(
  () => modelValue.value,
  (next) => {
    if (!isEqual(sortBy(selected.value), sortBy(next || []))) {
      selected.value = [...modelValue.value];
    }
  },
);
</script>

<template>
  <div v-if="filter.elements?.length" class="filter-grid">
    <div class="filter-grid__title">
      <VText class="filter-grid__title_name" font="title-medium">
        {{ filter.title }}
      </VText>
      <VText v-if="selected.length" font="body-large-accent" @click="onClear">
        Сбросить
      </VText>
      <VText
        v-if="modal && filter.elements.length > DEFAULT_ITEMS_DISPLAYED_COUNT"
        tag="div"
        font="body-small-accent"
        class="filter-grid__title_show-all"
        @click="onShowAll"
      >
        <span>Все</span>
        <ChevronRightIcon />
      </VText>
    </div>
    <VChipsGroup v-if="modal" wrapped>
      <VChips
        v-for="element in displayed"
        :key="element.name"
        v-model="selected"
        :value="element.name"
        wrapper-class="filter-grid__chips"
      >
        {{ element.name }}
      </VChips>
    </VChipsGroup>
    <div v-else :class="{ 'filter-grid_expanded': expanded }">
      <VSearch
        v-show="expanded"
        v-model="searchValue"
        :submit-visible="false"
        placeholder="Поиск значений"
        class="filter-grid__search"
      />
      <div class="filter-grid__wrapper">
        <VListItem
          v-for="element in displayed"
          :key="element.name"
          :divider="false"
          valign="center"
          class="filter-grid__list-item"
        >
          <VCheckbox v-model="selected" :value="element.name">
            <VText>{{ element.name }}</VText>
          </VCheckbox>
        </VListItem>
        <VText
          v-if="filter.elements.length && !displayed.length"
          font="body-large-regular"
          color="secondary"
          class="filter-grid__empty-search"
        >
          Ничего не нашлось
        </VText>
      </div>
      <div
        v-if="filter.elements.length > DEFAULT_ITEMS_DISPLAYED_COUNT"
        class="filter-grid__expand-button"
        @click="onToggleExpanded"
      >
        <VText font="body-large-accent">
          {{ expanded ? "Скрыть" : "Показать всё" }}
        </VText>
        <ChevronBottomIcon />
      </div>
    </div>
  </div>
</template>

<style lang="postcss" scoped>
.filter-grid {
  display: flex;
  flex-direction: column;
  max-height: 400px;
  overflow: auto;

  &__title {
    padding: var(--pl-unit-x2) var(--pl-unit-x2) var(--pl-unit-x2) 0;
    margin-top: var(--pl-unit-x4);
    display: flex;
    align-items: baseline;
    gap: var(--pl-unit-x2);

    &_name + * {
      cursor: pointer;
    }

    &_show-all {
      cursor: pointer;
      display: inline-flex;
      align-items: center;
      margin-left: auto;
      gap: var(--pl-unit-x2);
    }
  }

  &__chips {
    max-width: 100%;

    :deep(.pl-chips__decorator) {
      max-width: 100%;

      .pl-chips__text {
        text-overflow: ellipsis;
        word-wrap: break-word;
        overflow: hidden;
      }
    }
  }

  &__wrapper {
    flex-grow: 1;
    overflow: auto;
    padding-right: var(--pl-unit-x2);
  }

  &__search {
    box-sizing: border-box;
    padding: var(--pl-unit-x2) 0;
  }

  &__list-item {
    overflow: hidden;

    &:deep(.pl-checkbox__text) {
      word-break: break-word;
    }
  }

  &__empty-search {
    display: inline-block;
    padding: var(--pl-unit-x2) 0;
  }

  &__expand-button {
    padding: 6px 0;
    cursor: pointer;
    display: inline-flex;
    justify-content: left;
    align-items: center;
    gap: var(--pl-unit-x2);
  }

  &_expanded {
    display: flex;
    flex-direction: column;
    flex-grow: 1;
    overflow: auto;

    .filter-grid {
      &__expand-button {
        svg {
          transform: rotate(180deg);
        }
      }
    }
  }
}
</style>
